// What the header shows about the OpenAI spend of an admin: the text of the two amounts, how a trading-api answer or a
// failed request changes what is shown, and the view of it. Import-free CommonJS, so it can be tested in plain node --
// see src/lib/ai-optimization.js. Nothing here talks to the server or owns a timer.
//
// The figures are what OpenAI's Costs API reports and can lag by a few hours: an unknown number is a dash, and a number
// that could not be refreshed stays on screen but greyed. An error, from the server or from the request, is a small
// yellow exclamation icon right after the label with the reason on its own hover, while the hover on the amounts only
// says how old they are. The user asked for that icon at UAT 05.1 (gap G-05.1-1). That replaces, for the icon only, D-17
// ("No warning colour or threshold. The colour stays neutral always.") and this file's earlier rule from plan 05.1-05
// ("Never a warning colour or an alert."). Still never an alert or a toast.
//
// Phase 8 adds the live part: the estimate of the AI runs that are running now and of those that finished in the last
// 6 hours (the part OpenAI has not booked yet). Those numbers do not come from OpenAI but from the daemon's own token-usage estimate, which trading-api
// reads from its database on every request, so they have their own state and view next to the Costs figures and an
// OpenAI Costs error never greys them. The server decides which runs are running (heartbeat in the last 10 minutes) and
// which finished recently (last 6 hours); the browser only formats what it is told and never filters by time.

// trading-api caches the Costs figures for 5 minutes (CACHE_TTL_MS in trading-api src/lib/openai-spend.js), measured from
// the start of its last read. Polling exactly every 5 minutes let a poll that arrived a few ms early get the cached
// answer, so 'as of' moved only every 5 or 10 minutes (WR-61, UAT 05.1 gap G-05.1-1). The idle poll (every 10 minutes,
// SPEND-08) therefore stays at least 30 s above SERVER_CACHE_MS, so every idle poll lands on an expired Costs cache (the
// 05.1-08 rule). The fast poll (every 15 s, only while an AI run is queued or running) lands inside the cache on purpose:
// its point is the live estimates, which trading-api reads from its database on every request, while the Costs figures
// of those polls come from the same cache and never reach the OpenAI Costs API more often.
const SERVER_CACHE_MS = 300000;
const SPEND_IDLE_POLL_MS = 600000;
const SPEND_FAST_POLL_MS = 15000;

const SPEND_LABEL = 'OpenAI';
const DASH = '—';
const NO_FIGURES_REASON = 'OpenAI costs are not available right now';
const NO_ANSWER_REASON = 'the server did not answer';
const NO_FIGURES_TOOLTIP = 'no figures yet';
const LAG_NOTE = ' (OpenAI figures can lag by a few hours)';

const INITIAL_SPEND_STATE = { figures: null, reason: null };
const INITIAL_LIVE_STATE = { running: null, recent: null, queued: null, fundsHold: null, stale: false };

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);
const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

// '$1.50' for usd or an unknown currency, '2.00 EUR' for another 3-letter code, a dash for anything that is not a number
const formatSpend = (value, currency) => {
	if (!isFiniteNumber(value)) {
		return DASH;
	}
	const amount = value.toFixed(2);
	if (typeof currency === 'string' && /^[a-z]{3}$/i.test(currency) && currency.toLowerCase() !== 'usd') {
		return `${amount} ${currency.toUpperCase()}`;
	}
	return `$${amount}`;
};

// The new state after an outcome of getOpenAiSpend (apiAction: { ok, data } or { ok: false, error }). Figures are only
// replaced by an answer that has a month number; otherwise the last good figures stay and the reason says why
const nextSpendState = (previous, outcome) => {
	if (outcome?.ok) {
		const data = outcome.data || {};
		const error = typeof data.error === 'string' && data.error ? data.error : null;
		if (isFiniteNumber(data.month)) {
			return {
				figures: {
					today: isFiniteNumber(data.today) ? data.today : null,
					month: data.month,
					currency: typeof data.currency === 'string' ? data.currency : null,
					asOf: typeof data.asOf === 'string' ? data.asOf : null,
				},
				reason: error,
			};
		}
		return { figures: previous.figures, reason: error || NO_FIGURES_REASON };
	}
	const reason = (typeof outcome?.error === 'string' && outcome.error) || NO_ANSWER_REASON;
	return { figures: previous.figures, reason };
};

// { label, text, stale, tooltip, error } of a state; formatTime turns the asOf string into the clock text of the tooltip.
// The tooltip belongs to the amounts and only tells their age; the reason, if any, is the error
const spendView = (state, formatTime) => {
	const { figures, reason } = state;
	const error = reason || null;
	if (!figures) {
		return { label: SPEND_LABEL, text: DASH, stale: true, tooltip: reason ? NO_FIGURES_TOOLTIP : 'loading', error };
	}
	const today = formatSpend(figures.today, figures.currency);
	const month = formatSpend(figures.month, figures.currency);
	const text = `${today} today · ${month} month`;
	const asOf = figures.asOf ? `as of ${formatTime(figures.asOf)}` : 'as of an unknown time';
	return { label: SPEND_LABEL, text, stale: Boolean(reason), tooltip: `${asOf}${LAG_NOTE}`, error };
};

// ---- the live estimates (Phase 8) ----

// The text of an estimate in US dollars (the daemon's price table is USD, so unlike formatSpend this does not follow the
// Costs currency). The amount is rounded to whole cents half-up from the integer micro-dollars, not through a binary
// float toFixed of c / 1e6 (1.005 would print 1.00). A known zero is '$0.00', less than half a cent '<$0.01', anything
// else '$1.20' (no "~": at the small header font it reads as a minus, and the hovers already say it is estimated); a
// value that is not a finite number at or above 0 gives null, which the view shows as 'unknown', never $0
const formatEstimate = costMicroUsd => {
	if (!isFiniteNumber(costMicroUsd) || costMicroUsd < 0) {
		return null;
	}
	if (costMicroUsd === 0) {
		return '$0.00';
	}
	if (costMicroUsd < 5000) {
		return '<$0.01';
	}
	return `$${(Math.round(costMicroUsd / 10000) / 100).toFixed(2)}`;
};

const copyBlock = block => {
	if (!isPlainObject(block)) {
		return null;
	}
	return { runs: block.runs, costMicroUsd: block.costMicroUsd, unpriced: block.unpriced };
};

// The funds hold of trading-api 0.9.0 (Phase 9), { since, message, paused, waiting, strategies }, which the answer carries
// only while a hold exists. A copy, or null when the block is not a plain object or no run is paused: the header then
// shows no hold. The strategy names are kept as text only, in the order sent
const copyHold = block => {
	if (!isPlainObject(block) || !isFiniteNumber(block.paused) || block.paused <= 0) {
		return null;
	}
	return {
		since: typeof block.since === 'string' ? block.since : null,
		message: typeof block.message === 'string' ? block.message : '',
		paused: block.paused,
		waiting: isFiniteNumber(block.waiting) && block.waiting > 0 ? block.waiting : 0,
		strategies: Array.isArray(block.strategies) ? block.strategies.filter(name => typeof name === 'string') : [],
	};
};

// The new live state after an outcome of getOpenAiSpend. An ok answer with a running or recent block replaces all the
// blocks (a missing one is null; a missing queued is trading-api 0.8.0, a missing fundsHold is no hold). A failed
// request, or an ok answer without running and recent (trading-api's database read failed), keeps the last blocks but
// greyed, so a hiccup neither erases a figure nor passes an old one off as fresh
const nextLiveState = (previous, outcome) => {
	const data = outcome?.ok ? outcome.data : null;
	if (isPlainObject(data) && (isPlainObject(data.running) || isPlainObject(data.recent))) {
		return {
			running: copyBlock(data.running),
			recent: copyBlock(data.recent),
			queued: isPlainObject(data.queued) ? { runs: data.queued.runs } : null,
			fundsHold: copyHold(data.fundsHold),
			stale: false,
		};
	}
	return {
		running: previous.running,
		recent: previous.recent,
		queued: previous.queued,
		fundsHold: previous.fundsHold,
		stale: true,
	};
};

const runsText = runs => `${runs} AI ${runs === 1 ? 'run' : 'runs'}`;
const RUNNING_NOTE =
	' running, estimated from token usage (heartbeat in the last 10 minutes); OpenAI has not booked this yet';
const RECENT_NOTE =
	' finished in the last 6 hours, estimated from token usage; OpenAI may already have booked part of it in today and month, so do not add them up';
const STALE_NOTE = ' (could not be refreshed)';
const runningTooltip = runs => `${runsText(runs)}${RUNNING_NOTE}`;
const recentTooltip = runs => `${runsText(runs)}${RECENT_NOTE}`;

// One part of the second line, { key, text, tooltip, unknown }, or null when there is nothing to show (no block, or no
// run in it). 'unknown' when no run has an estimate yet (unpriced equal to runs) or a number is not usable, '+ ?' when
// some runs have none: never a complete-looking sum, and never $0.00 for an unpriced run. describe(runs) is the tooltip;
// missingTail ends the '; N without a cost estimate' note (a running run may still get one, a finished run will not).
// A stale state (the answer could not refresh the figures) ends the tooltip with ' (could not be refreshed)'
const livePart = (key, block, describe, missingTail, stale) => {
	if (!isPlainObject(block) || !isFiniteNumber(block.runs) || block.runs <= 0) {
		return null;
	}
	const { runs } = block;
	const unpriced = isFiniteNumber(block.unpriced) && block.unpriced >= 0 ? block.unpriced : null;
	const amount = formatEstimate(block.costMicroUsd);
	const unknown = amount === null || unpriced === null || unpriced >= runs;
	let text = `${key}: ${amount}`;
	if (unknown) {
		text = `${key}: unknown`;
	} else if (unpriced > 0) {
		text = `${key}: ${amount} + ?`;
	}
	const missing = unpriced !== null && unpriced > 0 ? `; ${unpriced} without a cost estimate${missingTail}` : '';
	return { key, text, tooltip: `${describe(runs)}${missing}${stale ? STALE_NOTE : ''}`, unknown };
};

// { running, recent, stale } of a live state. The parts are always in the order running, recent; a state that could not
// be refreshed is stale, which greys every part and ends each hover with ' (could not be refreshed)'. This follows the
// live state's own stale flag only, never spendView's, so an OpenAI Costs error does not grey a database figure
const liveView = live => {
	const stale = Boolean(live && live.stale);
	return {
		running: livePart('running', live?.running, runningTooltip, ' yet', stale),
		recent: livePart('recent', live?.recent, recentTooltip, '', stale),
		stale,
	};
};

// ---- the funds hold in the header (Phase 9) ----

const HOLD_ICON_TEXT = 'OpenAI has no funds: AI runs are paused';
const HOLD_FALLBACK_MESSAGE = 'OpenAI has no funds';
// How long the confirmation of the header's Continue stays in the popover (D-61)
const CONTINUE_NOTICE_MS = 4000;

// { iconText, message, countsText, strategiesText } of a live state, all plain text, or null when no hold is shown.
// message is the server's text, or 'OpenAI has no funds' when there is none; countsText is '1 AI run paused' or 'N AI runs paused', followed by ', M waiting' when M > 0; strategiesText is null
// when the server named none
const holdView = live => {
	const hold = live?.fundsHold;
	if (!hold) {
		return null;
	}
	const waiting = hold.waiting > 0 ? `, ${hold.waiting} waiting` : '';
	return {
		iconText: HOLD_ICON_TEXT,
		message: hold.message || HOLD_FALLBACK_MESSAGE,
		countsText: `${runsText(hold.paused)} paused${waiting}`,
		strategiesText: hold.strategies.length > 0 ? `Strategies: ${hold.strategies.join(', ')}` : null,
	};
};

// The inline confirmation of the header's Continue: show(text) tells onChange the text at once and arms ONE timer that
// tells it null after durationMs; a second show() cancels the first timer before it arms its own, and stop() cancels the
// timer and makes any later show() do nothing, so the owner can call it from an unmount cleanup. schedule is lib/timers'
// later(fn, ms), which returns { cancel }
const createInlineNotice = ({ schedule, durationMs, onChange }) => {
	let timer = null;
	let stopped = false;
	const cancel = () => {
		if (timer) {
			timer.cancel();
			timer = null;
		}
	};
	return {
		show(text) {
			if (stopped) {
				return;
			}
			cancel();
			onChange(text);
			timer = schedule(() => {
				timer = null;
				onChange(null);
			}, durationMs);
		},
		stop() {
			stopped = true;
			cancel();
		},
	};
};

const countRuns = block => (isPlainObject(block) && isFiniteNumber(block.runs) && block.runs > 0 ? block.runs : 0);

// The one place that decides whether the header polls fast: an AI run is queued or running. A missing block counts as 0
const isAiActive = live => countRuns(live?.queued) + countRuns(live?.running) > 0;

// true or false for an ok answer that carries the live blocks, null (leave the speed alone) for a failed request and for
// an ok answer without running and recent, so a network blip or a database hiccup does not drop the header to the idle
// speed in the middle of a run
const outcomeActivity = outcome => {
	if (!outcome?.ok) {
		return null;
	}
	const data = outcome.data;
	if (!isPlainObject(data) || (!isPlainObject(data.running) && !isPlainObject(data.recent))) {
		return null;
	}
	return isAiActive(data);
};

module.exports = {
	SPEND_IDLE_POLL_MS,
	SPEND_FAST_POLL_MS,
	SERVER_CACHE_MS,
	SPEND_LABEL,
	INITIAL_SPEND_STATE,
	INITIAL_LIVE_STATE,
	CONTINUE_NOTICE_MS,
	formatSpend,
	formatEstimate,
	nextSpendState,
	nextLiveState,
	spendView,
	liveView,
	holdView,
	createInlineNotice,
	isAiActive,
	outcomeActivity,
};
